import { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation } from 'react-router';
import { Action, Dispatch } from 'redux';

import { PaymentStatusEnum } from '../../lib/enums';
import { RootState } from '../../lib/store';
import { AccountLifecycleEnum, isAwaitingPayments } from '../../lib/store/account-flow/lifecycle';
import { getCurrentAccount } from '../../lib/store/crm/helpers';
import { CRMCall } from '../../lib/store/crm/types';
import { getSolanaPayments } from '../../lib/store/payment';
import { PaymentCall } from '../../lib/store/payment/types';
import { isCallStatusError, isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';

import { useElementVisibilityById, useIsMobile, useSolanaPendingPlanPayment } from '../../hooks';
import { UI_ROUTES } from '../../util/routes';
import AccountSetupNotification from '../AccountSetupNotification/AccountSetupNotification';
import AwaitingPaymentNotification from '../AwaitingPaymentNotification/AwaitingPaymentNotification';
import DocumentsReviewingNotification from '../DocumentsReviewingNotification/DocumentsReviewingNotification';
import NotVerifiedNotification from '../NotVerifiedNotification/NotVerifiedNotification';
import RewindTradingDayNotification from '../RewindTradingDayNotification/RewindTradingDayNotification';

import useAccountLifecycle from './useAccountLifecycle';

const NotificationsManager = ({
  currentAccount,
  isPaymentsAllLoaded,
  isPhoneConfirmed,
  isTradingPlansResolved,
  isRewindProcessing,
  fetchSolanaPayments,
  isSolanaPaymentPending,
  isUseDemoAccount,
}: ReduxProps) => {
  const location = useLocation();
  const { isMobile } = useIsMobile();

  const { isDemoSetup, lifecycle, phase } = useAccountLifecycle();

  const isSolanaPendingPlanPayment = useSolanaPendingPlanPayment();
  const isPlanTargetModalVisible = useElementVisibilityById('planTargetModal');

  const isBlockedByOtherNotification = isSolanaPendingPlanPayment || isPlanTargetModalVisible;

  const isPaymentsDataReady = isSolanaPaymentPending === false
    && !isAwaitingPayments(isPaymentsAllLoaded, currentAccount);
  const isLifecycleReadable = lifecycle === AccountLifecycleEnum.Creating
    || lifecycle === AccountLifecycleEnum.AwaitingApproval
    || (isPaymentsDataReady && isTradingPlansResolved);

  const isAccountNotificationVisible = !isBlockedByOtherNotification && isLifecycleReadable;

  const renderAccountNotification = () => {
    switch (lifecycle) {
      case AccountLifecycleEnum.AwaitingApproval:
        return isUseDemoAccount ? null : <DocumentsReviewingNotification />;
      case AccountLifecycleEnum.Creating:
      case AccountLifecycleEnum.SettingUp:
        return <AccountSetupNotification isDemoSetup={isDemoSetup} />;
      case AccountLifecycleEnum.FundedSettingUp:
        return <AccountSetupNotification isDemoSetup={isDemoSetup} phase={phase} />;
      case AccountLifecycleEnum.None:
      case AccountLifecycleEnum.Upsell:
        return <NotVerifiedNotification />;
      default:
        return null;
    }
  };

  useEffect(() => {
    fetchSolanaPayments();
  }, []);

  const isNotificationScreen = (
    isMobile
      ? [UI_ROUTES.dashboard, UI_ROUTES.overview, UI_ROUTES.discover]
      : [UI_ROUTES.dashboard, UI_ROUTES.overview, UI_ROUTES.discover, UI_ROUTES.tradeHistory]
  ).includes(location.pathname as UI_ROUTES);

  return (
    <>
      {isPhoneConfirmed && isNotificationScreen && (
        <>
          <AwaitingPaymentNotification isSolanaPendingPlanPayment={isSolanaPendingPlanPayment} />
          {isRewindProcessing && <RewindTradingDayNotification />}
          {isAccountNotificationVisible && renderAccountNotification()}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentAccount: getCurrentAccount(state),
  isUseDemoAccount: state.crm.isUseDemoAccount,
  isPaymentsAllLoaded: isCallStatusReady(PaymentCall.getPaymentsAll, state.payment.statusByCall),
  isPhoneConfirmed: state.crm.individualExtendedInfo?.phone_number_confirmed,
  isRewindProcessing: state.closeout.accountStatusData.dailyResetProcessing,
  isSolanaPaymentPending: state.payment.isSolanaPaymentPending,
  isTradingPlansResolved: isCallStatusReady(CRMCall.searchTradingPlans, state.crm.statusByCall)
    || isCallStatusError(CRMCall.searchTradingPlans, state.crm.statusByCall),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  fetchSolanaPayments: () =>
    dispatch(
      getSolanaPayments({
        statuses: [
          PaymentStatusEnum.Processed,
          PaymentStatusEnum.Completed,
          PaymentStatusEnum.Pending,
          PaymentStatusEnum.Canceled,
          PaymentStatusEnum.Rejected,
          PaymentStatusEnum.CanceledByClient,
        ],
      }),
    ),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(NotificationsManager);
